import React, { useState } from 'react';
import { clearCurrentEditedBomOption, setCurrentEditedBomOption, updateRecentBomOptionsList } from '../../../actions';
import {
  deselectLeafMultipleSelectionOption,
  deselectMultipleSelectionOption,
  selectLeafMultipleSelectionOption,
  selectMultipleSelectionOption,
  toggleSingleSelectionOption,
} from '../../../../bom/actions';
import {
  getChildOptionDict,
  getChildQuestion,
  getUniqueOptions,
  getWhetherNeedToDispatchBomColorChangeEvent,
} from '../../../../bom/selectors';
import { getCurrentEditedBomOption, getMostRecentlySelectedBomOption } from '../../../product/selectors';
import { useDispatch, useSelector } from 'react-redux';

import AdditionalSwatchSelector from '../AdditionalSwatchSelector';
import AvailableChildOptions from '../AvailableChildOptions';
import ChildQuantityGroup from '../ChildQuantityGroup';
import ParentOptionGroup from '../ParentOptionGroup';
import PropTypes from 'prop-types';
import deepEqual from 'deep-equal';
import { dispatchBomColorChangeEvent } from '../../../../bom/logics';
import { updateCurrentGalleryItemAsync } from '../../../product/logics';

const GraphicParentChildQuestion = ({ feedback, parentQuestion, resetFeedback }) => {
  const dispatch = useDispatch();

  const parentOptions = useSelector((state) => getUniqueOptions(state, parentQuestion.code));
  const childQuestion = useSelector((state) => getChildQuestion(state, parentQuestion.code));
  const childOptionDict = useSelector((state) => getChildOptionDict(state, childQuestion.code));
  const needToDispatchBomColorChangeEvent = useSelector((state) =>
    getWhetherNeedToDispatchBomColorChangeEvent(state, parentQuestion.code)
  );
  const currentEditedBomOption = useSelector(getCurrentEditedBomOption);
  const mostRecentlySelectedBomOption = useSelector((state) =>
    getMostRecentlySelectedBomOption(state, parentQuestion.code)
  );

  const [previewOptionIndex, setPreviewOptionIndex] = useState(-1);
  const [showPreviousSelectionNotAvailableWarning, setShowPreviousSelectionNotAvailableWarning] = useState(false);

  const hasMultipleParentOptions = parentOptions.length > 1;
  const hasMultipleSelectableParentOptions = parentOptions.filter((option) => !option.isSoldOut).length > 1;
  const previewable = hasMultipleParentOptions;
  const hasParentOptionSelected = parentOptions.some((option) => option.isSelected);
  const previewOption = previewOptionIndex >= 0 ? parentOptions[previewOptionIndex] : null;
  const showPreview = !!previewOption && !previewOption.isSelected;
  const hasAvailableParentOptions = parentOptions.some((option) => !option.isSelected && !option.isSoldOut);

  const handleDispatchBomColorChangeEvent = (isMultiSelect, option) => {
    if (needToDispatchBomColorChangeEvent) {
      dispatchBomColorChangeEvent({ isMultiSelect, option });
    }
  };

  const handlePreviewOption = (index) => {
    setPreviewOptionIndex(index);
  };

  const handleSelectOption = (multiSelectMode, parentOption) => {
    let dispatchedOption;
    let multiSelect = multiSelectMode;

    if (multiSelectMode) {
      if (parentOption.isSelected) {
        dispatch(deselectMultipleSelectionOption(parentOption.code, parentOption.codeValue));
        dispatch(deselectLeafMultipleSelectionOption(childQuestion.code, parentOption.codeValue));
        if (!!mostRecentlySelectedBomOption && mostRecentlySelectedBomOption.codeValue === parentOption.codeValue) {
          setShowPreviousSelectionNotAvailableWarning(false);
        }
      } else {
        dispatch(selectMultipleSelectionOption(parentOption.code, parentOption.codeValue, 0));
        setShowPreviousSelectionNotAvailableWarning(false);
      }
    } else {
      if (parentOption.isSelected) {
        return;
      }

      const hasMultipleParentOptionsSelected = parentOptions.filter((option) => option.isSelected).length > 1;
      const hasQuantitiesEntered = parentOptions.some(
        (option) =>
          option.isSelected &&
          childOptionDict[option.codeValue].some((childOption) => childOption.isSelected && childOption.quantity > 0)
      );
      if (hasMultipleParentOptionsSelected || hasQuantitiesEntered) {
        if (!!mostRecentlySelectedBomOption) {
          const targetCodeValues = childOptionDict[parentOption.codeValue]
            .filter((option) => !option.isSoldOut)
            .map((option) => option.codeValue);
          const mostRecentlyEnteredQuantities = Object.fromEntries(
            childOptionDict[mostRecentlySelectedBomOption.codeValue]
              .filter((option) => option.isSelected && option.quantity > 0)
              .map((option) => [option.codeValue, option.quantity])
          );
          const carriedOverQuantities = Object.fromEntries(
            childOptionDict[mostRecentlySelectedBomOption.codeValue]
              .filter(
                (option) => option.isSelected && option.quantity > 0 && targetCodeValues.includes(option.codeValue)
              )
              .map((option) => [option.codeValue, option.quantity])
          );

          dispatchedOption = { ...mostRecentlySelectedBomOption, isSelected: false };
          multiSelect = true;
          dispatch(
            deselectMultipleSelectionOption(mostRecentlySelectedBomOption.code, mostRecentlySelectedBomOption.codeValue)
          );
          if (Object.entries(mostRecentlyEnteredQuantities).length > 0) {
            dispatch(deselectLeafMultipleSelectionOption(childQuestion.code, mostRecentlySelectedBomOption.codeValue));
          }
          dispatch(updateRecentBomOptionsList({ multiSelect, option: dispatchedOption }));
          handleDispatchBomColorChangeEvent(multiSelect, dispatchedOption);

          dispatch(selectMultipleSelectionOption(parentOption.code, parentOption.codeValue, 0));
          if (Object.entries(carriedOverQuantities).length > 0) {
            dispatch(
              selectLeafMultipleSelectionOption(childQuestion.code, parentOption.codeValue, carriedOverQuantities)
            );
          }

          setShowPreviousSelectionNotAvailableWarning(
            Object.entries(mostRecentlyEnteredQuantities).length > 0 &&
              !deepEqual(mostRecentlyEnteredQuantities, carriedOverQuantities)
          );
        }
      } else {
        dispatch(toggleSingleSelectionOption(parentOption.code, parentOption.codeValue));
        setShowPreviousSelectionNotAvailableWarning(false);
      }
    }
    resetFeedback(parentOption.code);
    resetFeedback('quantity');

    dispatchedOption = { ...parentOption, isSelected: !parentOption.isSelected };
    dispatch(dispatchedOption.isSelected ? setCurrentEditedBomOption(dispatchedOption) : clearCurrentEditedBomOption());
    dispatch(updateRecentBomOptionsList({ multiSelect, option: dispatchedOption }));
    dispatch(updateCurrentGalleryItemAsync(dispatchedOption));
    handleDispatchBomColorChangeEvent(multiSelect, dispatchedOption);
  };

  const handleRemoveOption = (parentOption) => {
    dispatch(deselectMultipleSelectionOption(parentOption.code, parentOption.codeValue));
    dispatch(deselectLeafMultipleSelectionOption(childQuestion.code, parentOption.codeValue));
    if (!!mostRecentlySelectedBomOption && mostRecentlySelectedBomOption.codeValue === parentOption.codeValue) {
      setShowPreviousSelectionNotAvailableWarning(false);
    }
    resetFeedback(parentOption.code);
    resetFeedback('quantity');

    const multiSelect = true;
    const dispatchedOption = { ...parentOption, isSelected: false };
    if (!!currentEditedBomOption && currentEditedBomOption.codeValue === dispatchedOption.codeValue) {
      dispatch(clearCurrentEditedBomOption());
    }
    dispatch(updateRecentBomOptionsList({ multiSelect, option: dispatchedOption }));
    dispatch(updateCurrentGalleryItemAsync(dispatchedOption));
    handleDispatchBomColorChangeEvent(multiSelect, dispatchedOption);
  };

  return (
    <>
      <ParentOptionGroup
        feedback={feedback}
        options={parentOptions}
        previewable={previewable}
        question={parentQuestion}
        onPreviewOption={hasMultipleSelectableParentOptions ? handlePreviewOption : undefined}
        onSelectOption={hasMultipleSelectableParentOptions ? handleSelectOption : undefined}
      />
      {showPreview && (
        <AvailableChildOptions
          heading={`Options Available - ${previewOption.description}`}
          options={childOptionDict[previewOption.codeValue]}
        />
      )}
      {hasParentOptionSelected && (
        <>
          <hr />
          <ChildQuantityGroup
            childQuestion={childQuestion}
            feedback={feedback}
            parentOptions={parentOptions}
            parentQuestion={parentQuestion}
            resetFeedback={resetFeedback}
            showPreviousSelectionNotAvailableWarning={showPreviousSelectionNotAvailableWarning}
            onRemoveOption={hasMultipleSelectableParentOptions ? handleRemoveOption : undefined}
          />
          {hasAvailableParentOptions && (
            <AdditionalSwatchSelector question={parentQuestion} onSelectOption={handleSelectOption} />
          )}
        </>
      )}
    </>
  );
};

GraphicParentChildQuestion.propTypes = {
  feedback: PropTypes.objectOf(PropTypes.string).isRequired,
  parentQuestion: PropTypes.shape({
    code: PropTypes.string.isRequired,
    isRealParentChildQuestion: PropTypes.bool.isRequired,
    itemId: PropTypes.string.isRequired,
    mandatoryFlag: PropTypes.string.isRequired,
    needToReloadUniqueOptions: PropTypes.bool.isRequired,
    questionId: PropTypes.number.isRequired,
    questionLabel: PropTypes.string.isRequired,
    questionText: PropTypes.string.isRequired,
    questionType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
    representationType: PropTypes.oneOf([0, 1]).isRequired,
    sampleQuestionText: PropTypes.string,
    selectionType: PropTypes.oneOf([0, 1]).isRequired,
    sequence: PropTypes.number.isRequired,
    thumbnailType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
  }).isRequired,
  resetFeedback: PropTypes.func.isRequired,
};

export default GraphicParentChildQuestion;
